import { AfterViewInit, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import * as am5plugins_sliceGrouper from '@amcharts/amcharts5/plugins/sliceGrouper';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Semi Circle Pie Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-semi-circle-pie-graph',
  standalone: false,
  templateUrl: './semi-circle-pie-graph.component.html',
  styleUrl: './semi-circle-pie-graph.component.scss'
})
export class SemiCirclePieGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private semiCirclePieGraphReportSubscription: any;
  private isSemiCirclePieViewInitialized = false;

  @Input() reportData: any = null;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _spinnerService: SpinnerService,
    private _commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.semiCirclePieGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Semi Circle Pie Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isSemiCirclePieViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isSemiCirclePieViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  createChart(data: BarChartData[]) {
    // Check if the chart container exists
    const chartContainer = document.getElementById('semi-circle-pie-chartdiv');
    if (!chartContainer) {
      console.error('Semi-Circle Pie Chart - Chart container not found! Make sure element with id "semi-circle-pie-chartdiv" exists');
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Semi-Circle Pie Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Semi-Circle Pie Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('semi-circle-pie-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'semi-circle-pie',
        data: data
      });

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      // Create chart with proper semi-circle configuration
      const pieChart = this.root.container.children.push(am5percent.PieChart.new(this.root, {
        startAngle: 180,
        endAngle: 360,
        layout: this.root.verticalLayout,
        innerRadius: am5.percent(50)
      }));

      // Enhanced chart animation
      this._baseChartService.setupChartAnimation(pieChart, 'elastic');

      let outermostSeries: am5percent.PieSeries | undefined;

      valueFields.forEach((valueField, index) => {
        // Create series with proper semi-circle configuration
        let pieSeries = pieChart.series.push(
          am5percent.PieSeries.new(this.root, {
            startAngle: 180,
            endAngle: 360,
            valueField: valueField,
            categoryField: categoryField,
            name: valueField,
            alignLabels: false
          })
        );
         

        // Enhanced series animation
        this._baseChartService.setupSeriesAnimation(pieSeries, 'bounce');

        if (index === valueFields.length - 1) {
          outermostSeries = pieSeries;
        }

        // Create hidden state
        pieSeries.states.create("hidden", {
          startAngle: 180,
          endAngle: 180
        });

        // Configure slices with corner radius
        pieSeries.slices.template.setAll({
          stroke: am5.color("#fff"),
          cornerRadius: 5
        });

        // Configure tooltip properly with enhanced animation
        /**
         * @author: Pavan | 07-05-2026
         * @description: Commented the tooltip related code as it ovelapping when over other slices
        const tooltip = this._baseChartService.createStandardTooltip(this.root, 'pie', {
          showPercentage: true,
          showValue: true,
          showCategory: true
        });
        pieSeries.slices.template.set('tooltip', tooltip);  */

        // Hide labels and ticks in all states (including when "Others" is clicked/drilled)
        // Show info only via tooltip on hover
        pieSeries.labels.template.setAll({
          forceHidden: true
        });
        pieSeries.ticks.template.setAll({
          forceHidden: true
        });

        pieSeries.data.setAll(data);

        // Add slice grouper plugin - groups slices beyond threshold into "Others"
        am5plugins_sliceGrouper.SliceGrouper.new(this.root, {
          series: pieSeries,
          clickBehavior: "break",
          threshold: 5,
          groupName: "Others"
        });
      });

      let legend = pieChart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      // Enhanced legend animation
      this._baseChartService.setupLegendAnimation(legend);

      const updateLegend = () => {
        if (outermostSeries) {
          legend.data.setAll(outermostSeries.dataItems);
        }
      };

      setTimeout(() => updateLegend(), 100);

      if (outermostSeries) {
        outermostSeries.slices.template.events.on("click", () => {
          setTimeout(() => updateLegend(), 50);
        });
      }

      // Trigger enhanced chart animation
      setTimeout(() => {
        pieChart.appear(1200, 100);
      }, 100);

      console.log('Semi-Circle Pie Chart - Chart created successfully');

    } catch (error) {
      console.error('Error creating semi-circle pie chart:', error);
    }
  }
  private updateChart(data: BarChartData[]) {
    // Dispose existing chart first
    if (this.root) {
      console.log('Semi Circle Pie Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    // Hide spinner if it's still showing
    this._spinnerService.hide();
    this._baseChartService.disposeChart(this.root);
    if (this.semiCirclePieGraphReportSubscription) {
      this.semiCirclePieGraphReportSubscription.unsubscribe();
    }
  }
}
